import { groupBy } from '../../helpers/array.helper';
import { SelectGroup, SelectItem, SelectItemType } from './select-item.type';
import { Selection } from './selection';

export const SELECT_ALL_ID = '__SELECT_ALL__';

export class SelectWithSearchHelper {
  static createSelectAllItem(selectAllLabel: string | undefined, multiple: boolean): SelectItem | null {
    if (!selectAllLabel || !multiple) {
      return null;
    }

    return {
      id: SELECT_ALL_ID,
      label: selectAllLabel,
      type: SelectItemType.All,
      isFixed: true,
    };
  }

  static getFixedItemsWithoutGroup(fixedItems: SelectItem[] | null | undefined): SelectItem[] {
    return (fixedItems ?? []).filter(item => !item.group);
  }

  static areAllItemsSelected(selectedItems: SelectItem[], items: SelectItem[] | null | undefined): boolean {
    const regularItems = items ?? [];
    if (regularItems.length === 0) {
      return false;
    }

    const ids = new Set(selectedItems.map(item => item?.id));
    return regularItems.every(item => ids.has(item.id));
  }

  static normalizeToArray(value: string | string[] | null | undefined): string[] {
    if (value == null) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  static sanitizeSelection(selection: Selection | null | undefined): Selection | null {
    if (selection == null) {
      return null;
    }

    return Array.isArray(selection) ? selection.filter((item): item is SelectItem => item != null) : selection;
  }

  static buildGroups(
    items: SelectItem[] | null | undefined,
    fixedItems: SelectItem[] | null | undefined,
    filter: string,
    sortItemsByLabel: boolean,
  ): SelectGroup[] {
    const regularItems = items ?? [];
    const filterValue = filter.toLowerCase();
    const filteredItems = filter === '' ? regularItems : regularItems.filter(item => item?.label?.toLowerCase().includes(filterValue));

    const fixedItemsWithGroups = (fixedItems ?? []).filter(item => item.group != null);
    const fixedItemsByGroup = groupBy(fixedItemsWithGroups, item => item?.group);
    const groupedItems = groupBy(filteredItems, item => item?.group);
    const allGroupLabels = new Set([...groupedItems.keys(), ...fixedItemsByGroup.keys()]);

    return Array.from(allGroupLabels)
      .map(label => {
        const groupedFixedItems = this.sortItems(fixedItemsByGroup.get(label) ?? [], sortItemsByLabel);
        const groupedRegularItems = this.sortItems(groupedItems.get(label) ?? [], sortItemsByLabel);

        return {
          label,
          items: [...groupedFixedItems, ...groupedRegularItems],
          fixedItems: groupedFixedItems,
        };
      })
      .sort((left, right) => left.label?.localeCompare(right.label) || 0);
  }

  static getTriggerLabel(selection: Selection | null | undefined, items: SelectItem[] | null | undefined, sortItemsByLabel: boolean): string | null {
    if (!Array.isArray(selection) || items == null) {
      return null;
    }

    const selectAllTypeItem = selection.find(item => item?.type === SelectItemType.All);
    const explicitItems = selection.filter(item => item?.type === SelectItemType.Explicit);

    if (selectAllTypeItem != null) {
      const labels = [selectAllTypeItem.label];

      if (explicitItems.length > 0) {
        labels.push(...this.sortItems(explicitItems, sortItemsByLabel).map(item => item.label));
      }

      return labels.join(', ');
    }

    const itemsInLabel = selection.filter(item => item?.type !== SelectItemType.All);
    return itemsInLabel.length > 0 ? this.sortItems(itemsInLabel, sortItemsByLabel).map(item => item?.label).join(', ') : null;
  }

  static syncFixedItems(
    selection: Selection | null | undefined,
    items: SelectItem[] | null | undefined,
    fixedItems: SelectItem[] | null | undefined,
    selectAllItem: SelectItem | null,
    multiple: boolean,
  ): Selection | null {
    if (!selection || !items || !multiple) {
      return selection ?? null;
    }

    const selectionArray = Array.isArray(selection) ? selection : [selection];
    // Only create a new array if there are null items to remove; otherwise preserve the reference
    const hasNulls = selectionArray.some(item => item == null);
    let selectedItems: SelectItem[] = hasNulls ? selectionArray.filter((item): item is SelectItem => item != null) : (selectionArray as SelectItem[]);
    const selectAllTypeItems = this.getSelectAllTypeItems(selectAllItem, fixedItems);
    const allItemsSelected = this.areAllItemsSelected(selectedItems, items);

    selectAllTypeItems.forEach(item => {
      const isSelected = selectedItems.some(selectedItem => selectedItem?.id === item.id);

      if (allItemsSelected && !isSelected) {
        selectedItems.push(item);
      } else if (!allItemsSelected && isSelected) {
        selectedItems = selectedItems.filter(selectedItem => selectedItem.id !== item.id);
      }
    });

    return this.syncExclusiveItem(selectedItems, fixedItems);
  }

  static syncExclusiveItem(selectedItems: SelectItem[], fixedItems: SelectItem[] | null | undefined): SelectItem[] {
    const exclusiveItem = fixedItems?.find(item => item.type === SelectItemType.Exclusive);
    if (!exclusiveItem) {
      return selectedItems;
    }

    const otherItems = selectedItems.filter(item => item.type !== SelectItemType.Exclusive);
    return otherItems.length > 0 ? otherItems : [exclusiveItem];
  }

  static getSelectAllTypeItems(selectAllItem: SelectItem | null, fixedItems: SelectItem[] | null | undefined): SelectItem[] {
    const items: SelectItem[] = [];

    if (selectAllItem) {
      items.push(selectAllItem);
    }

    if (fixedItems?.length) {
      items.push(...fixedItems.filter(item => item.type === SelectItemType.All));
    }

    return items;
  }

  static getVisibleRegularItems(items: SelectItem[] | null | undefined, filter: string): SelectItem[] {
    const filterValue = filter.toLowerCase();
    if (!filterValue) {
      return items ?? [];
    }

    return (items ?? []).filter(item => item?.label?.toLowerCase().includes(filterValue));
  }

  static hasSelectionChanged(current: Selection | null | undefined, synced: Selection | null | undefined): boolean {
    const currentIds = this.getIdsFromSelection(current).sort();
    const syncedIds = this.getIdsFromSelection(synced).sort();
    return currentIds.length !== syncedIds.length || !currentIds.every((id, index) => id === syncedIds[index]);
  }

  static getSelectionIds(selection: Selection | null | undefined, multiple: boolean): string | string[] | null {
    if (!selection) {
      return multiple ? [] : null;
    }

    if (Array.isArray(selection)) {
      return selection
        .filter(item => item != null && item.id !== SELECT_ALL_ID)
        .map(item => item.id);
    }

    return selection.id;
  }

  static getSelectionFromIds(
    ids: string | string[] | null | undefined,
    items: SelectItem[] | null | undefined,
    fixedItems: SelectItem[] | null | undefined,
    selectAllItem: SelectItem | null,
    multiple: boolean,
  ): Selection | null {
    if (ids == null) {
      return multiple ? [] : null;
    }

    const regularItems = items ?? [];
    const staticItems = fixedItems ?? [];
    const allItems = [...regularItems, ...staticItems];

    if (allItems.length === 0) {
      return multiple ? [] : null;
    }

    if (Array.isArray(ids)) {
      const idSet = new Set(ids);
      const selectedItems = allItems.filter(item => item && idSet.has(item.id));

      if (selectAllItem && multiple && this.areAllItemsSelected(selectedItems, regularItems)) {
        return [selectAllItem, ...selectedItems];
      }

      return selectedItems;
    }

    return allItems.find(item => item && item.id === ids) || null;
  }

  private static sortItems(items: SelectItem[], sortItemsByLabel: boolean): SelectItem[] {
    return sortItemsByLabel ? [...items].sort((left, right) => left.label?.localeCompare(right.label) ?? 0) : items;
  }

  private static getIdsFromSelection(selection: Selection | null | undefined): string[] {
    if (!selection) {
      return [];
    }

    const items = Array.isArray(selection) ? selection : [selection];
    return items.map(item => item?.id).filter(Boolean);
  }
}